/* eslint-disable camelcase */
import Vue from 'vue'

import { instance } from '@/api'
import { getAll } from '@/api/request'
import OrderController from '@/controllers/production/Order/OrderController'
import PositionController from '@/controllers/production/Position/PositionController'
import StageLineController from '@/controllers/production/StageLine/StageLineController'

import { formulaTotalWeight } from '@/utils/production/position/formulas'
import { fullName } from '@/utils/string'
import { defaultStageList, StageItemModel } from '@/classes/StageItemModel'
import { fixedNumberCopy } from '@/utils/number'
import { setAlertError, setAlertSuccess } from '@/utils/store/alert'
import { goToPage } from '@/utils/router'
import { getUserItem } from '@/utils/model/User'
import { syncItemById } from '@/utils/array/reactive'
import PositionApi from '@/api/production/Position/PositionApi'
import { positionModel } from '@/entities/position'
import { GOST_URL } from '@/api/handbook/warehouse/GOSTApi'
import PackingController from '@/controllers/production/Packing/PackingController'
import { PACKING_STATUSES, TYPE_INDICATORS } from '@/entities/order/model'
import MaterialController from '@/controllers/warehouse/Material/MaterialController'
import ConsignmentController from '@/controllers/production/Consignment/ConsignmentController'
import { calcFreeAmount } from '@/utils/warehouse/material'
import { getResponseList, getResponsePayload } from '@/utils/apiResponse'

const state = () => ({
  order: null,
  orderLoading: false,

  currentPosition: {},
  currentPositionFormDataFilesDeleted: [],
  positions: [],
  activePositionIndex: 0,
  currentPositionId: null,
  isEditPosition: false,

  positionStatusLine: {},
  stageStore: {},
  statuses: [],
  tempStatuses: [],
  isCreateStage: false,
  isEditStage: false,
  currentStageListForConsignment: [],

  consignments: [],

  comments: [],

  gosts: [],

  packing: [],
  packingPrepare: [],
  packingPositionsCount: {},
  packingCount: 0,

  materialForReserve: []
})

const getters = {
  order: state => state.order,
  currentOrderId: state => state.order.id,
  currentOrderDeliveryPermission: state => state.order.delivery_permission,
  orderLoading: state => state.orderLoading,
  orderStatusId: state => state.order.status_id,

  positions: state => {
    return state.positions
  },
  tabsPositions: state => {
    if (!state.positions?.length) return []

    return state.positions.map(item => ({
      id: item.id,
      title: `№${item.sort} ${item.type.name}`,
      count: item.value
      // countPacking: state.packingPositionsCount[item.id] ?? null
    }))
  },

  packingPositions: state => {
    if (!state.positions?.length) return []

    return state.positions.map(item => ({
      id: item.id,
      title: `№${item.sort} ${item.type.name}`,
      value: item.value,
      packingValue: state.packingPositionsCount[item.id] ?? null
    }))
  },
  copyPositions: state => {
    if (!state.positions?.length) return []

    return state.positions.map(item => ({
      id: item.id,
      name: `№${item.sort} ${item.type.name} (${item.value})`
    }))
  },
  currentPosition: state => state.currentPosition,
  currentPositionActive: state => state.positions[state.activePositionIndex],
  activePositionIndex: state => state.activePositionIndex,
  currentPositionId: state => state.currentPositionId,
  isEditPosition: state => state.isEditPosition,

  statuses: state => {
    return state.statuses.map(status => ({
      ...status,
      user: getUserItem(status.user)
    }))
  },
  positionStatusLine: state => state.positionStatusLine,
  currentStageList: state => {
    return state.statuses.length
      ? state.statuses.map(item => item.status)
      : []
  },

  positionConsignments (state) {
    return state.consignments || []
  },
  positionConsignmentsLength: (_, getters) => getters.positionConsignments.length,

  totalCountConsignments (getters) {
    return getters.positionConsignments.reduce((previousValue, currentValue) => {
      return previousValue + +currentValue.value
    }, 0) || 0
  },

  totalDirtyWeightConsignments (state) {
    return positionModel.calcConsignmentsDirtyWeight(state.currentPosition)
  },

  stageStore (state) {
    return state.positionStatusLine.statuses?.map((stage) => {
      const stageStatusId = stage.status?.id ?? stage.status_id

      return {
        name: stage.status?.name,
        user: stage.user,
        listOfConsignment:
          state.currentPosition?.consignments?.filter(c => {
            const consignmentStatusId = c.status?.id ?? c.status_id
            return !!stageStatusId && !!consignmentStatusId && consignmentStatusId === stageStatusId
          })
      }
    }) || []
  },

  isEditStage: state => state.isEditStage,
  isCreateStage: state => state.isCreateStage,

  comments: state => state.comments,

  getGostByMaterialId: state => id => {
    return state.gosts?.filter(item => item.materials?.some(material => material.id === id))
  },

  materialForReserve: state => {
    return state.materialForReserve.map((item) => {
      const freeCount = Math.floor(calcFreeAmount(item) / item.weight_by_one) || 0
      return {
        ...item,
        length_and_count: item.length + ' - ' + (freeCount < 0 ? 0 : freeCount) + ' шт.'
      }
    })
  }
}

const mutations = {
  SET_ONE_ORDER (state, order) {
    if (order.positions?.length) {
      order.positions = order.positions.map(position => {
        const name = fullName(position.responsible?.surname, position.responsible?.name)
        const responsible = position.responsible ? {
          id: position.responsible?.id,
          name
        } : null

        return { ...position, responsible }
      })
    }

    state.order = order
  },
  UPDATE_ORDER (state, order) {
    state.order = { ...order }
  },
  SET_ORDER_LOADING (state, loading) {
    state.orderLoading = loading
  },
  SET_ORDER_READY_FOR_SHIPMENT_MARKERS (state, { is_pickup, documents_are_ready }) {
    state.order.is_pickup = is_pickup
    state.order.documents_are_ready = documents_are_ready
  },
  SET_POSITIONS (state, positions) {
    state.positions = Array.isArray(positions) ? positions : []
  },

  ADD_POSITION (state, payload) {
    state.positions.push(payload)
  },
  REMOVE_POSITION (state, id) {
    state.positions = state.positions.filter(item => +item.id !== +id)
  },
  SET_POSITION_ACTIVE_INDEX (state, index) {
    state.activePositionIndex = index
  },
  SET_CURRENT_POSITION (state, index) {
    state.currentPositionFormDataFilesDeleted = []
    state.currentPosition = { ...state.positions[index] } || null
    state.positionStatusLine = { ...state.positions[index]?.statuses_line } || {}

    state.isCreateStage = !state.positions[index]?.statuses_line?.statuses
    state.isEditStage = false
  },
  SET_CURRENT_POSITION_CANCEL_EDIT (state) {
    state.currentPosition = { ...state.positions[state.activePositionIndex] } || null
    state.isEditPosition = false
  },
  SET_CURRENT_POSITION_ID (state, index = 0) {
    if (state.positions) {
      state.currentPositionId = state.positions[index]?.id
    }
  },
  SET_CURRENT_POSITION_VALUE (state, { value, id, isChangeWeight }) {
    state.currentPosition = {}
    state.currentPosition = { ...value }
    if (id) {
      const index = state.positions.findIndex(item => item.id === id)
      if (index !== -1) {
        Vue.set(
          state.positions,
          index,
          { ...state.positions[index], ...value }
        )

        if (isChangeWeight) {
          state.consignments = state.positions[index]?.consignments || []
        }
      }
    }
  },
  SET_CURRENT_POSITION_ACTIVE_VALUE (state, { data, index }) {
    Vue.set(
      state.positions,
      index,
      { ...state.positions[index], ...data }
    )
  },
  TOGGLE_IS_EDIT_POSITION (state) {
    state.isEditPosition = !state.isEditPosition
  },
  CHANGE_IS_EDIT_POSITION (state, payload) {
    state.isEditPosition = payload
  },
  SET_CURRENT_POSITION_STATUS_LINE (state, payload) {
    state.positions[state.activePositionIndex].statuses_line = { ...payload }
  },
  SET_CURRENT_POSITION_RESPONSIBLE (state, payload) {
    state.currentPosition.responsible = { ...payload }
    state.positions[state.activePositionIndex].responsible = { ...payload }
  },
  SET_CURRENT_POSITION_NAME (state, payload) {
    state.positions[state.activePositionIndex].name = payload
  },

  SET_CURRENT_POSITION_WORKSHOP (state, payload) {
    state.positions[state.activePositionIndex].workshop = payload
    state.positions[state.activePositionIndex].workshop_id = payload.id
    state.currentPosition.workshop = payload
    state.currentPosition.workshop_id = payload.id
  },

  SET_CURRENT_POSITION_FILES_DELETE (state, id) {
    if (!id) {
      state.currentPositionFormDataFilesDeleted = []
      return
    }
    state.currentPositionFormDataFilesDeleted.push(id)
  },

  SET_CURRENT_POSITION_INDICATORS (state, data) {
    const indicatorsPosition = TYPE_INDICATORS.filter(item => item.showPosition === true)
    indicatorsPosition.forEach(indicator => {
      state.positions[state.activePositionIndex][indicator.name] = data[indicator.name]
      state.currentPosition[indicator.name] = data[indicator.name]
    })
  },

  RESET_CURRENT_POSITION_INDICATORS (state) {
    const indicatorsPosition = TYPE_INDICATORS.filter(item => item.showPosition === true)
    indicatorsPosition.forEach(indicator => {
      state.positions[state.activePositionIndex][indicator.name] = false
      state.currentPosition[indicator.name] = false
    })
  },

  SET_CURRENT_POSITION_MATERIAL_CHILDREN (state, material) {
    state.positions[state.activePositionIndex].material.children = material?.children || []
  },

  // stage
  TOGGLE_IS_EDIT_STAGE (state) { state.isEditStage = !state.isEditStage },
  CHANGE_IS_EDIT_STAGE (state, payload) { state.isEditStage = payload },
  CHANGE_IS_CREATE_STAGE (state, payload) { state.isCreateStage = payload },
  SET_STAGE_STORE (state, stages) {
    state.stageStore = {}
    state.stageStore = stages
  },
  ADD_STATUS_BY_STATUS_LINE (state, index) {
    state.statuses.splice(state.statuses.length - 3, 0, new StageItemModel(index))
  },
  SET_POSITION_STATUS_LINE (state, index) {
    if (state.positions && state.positions[index]?.statuses_line) {
      state.statuses = [...state.positions[index]?.statuses_line.statuses]
      state.tempStatuses = JSON.parse(JSON.stringify([...state.positions[index]?.statuses_line.statuses]))
    } else {
      state.statuses = [...defaultStageList]
    }
  },
  SET_POSITION_STATUS_LINE_VALUE (state, value) {
    if (value.index || value.index === 0) {
      const data = {
        sort: value.index + 1,
        status: value.data.status,
        user: getUserItem(value.data.user)
      }
      state.statuses[value.index] = { ...data }
    } else {
      state.statuses = value || [...defaultStageList]
    }
  },
  SET_POSITION_STATUS_LINE_VALUE_TEMP (state, value) {
    state.tempStatuses = value
  },

  // consignment
  SET_CONSIGNMENTS (state, index) {
    state.consignments = state.positions[index]?.consignments || []
  },
  ADD_CONSIGNMENT (state, payload) {
    state.consignments.push(payload)
  },
  UPDATE_CONSIGNMENT_AFTER_SPLIT (state, payload) {
    const index = state.consignments.findIndex((elem) => elem.id === payload.id)

    state.consignments[index].value = state.consignments[index].value - payload.value
    if (state.consignments[index].weight_dirty) {
      const weight_dirty = state.consignments[index].weight_dirty - payload.weight_dirty
      state.consignments[index].weight_dirty = fixedNumberCopy(weight_dirty, 3)
    }
  },
  UPDATE_CONSIGNMENT (state, consignment) {
    syncItemById(consignment, state.consignments)
  },
  REMOVE_CONSIGNMENT (state, id) {
    const index = state.consignments.findIndex((elem) => elem.id === id)
    Vue.delete(state.consignments, index)
  },
  CLEAR_ORDER (state) {
    state.order = null
  },

  CLEAR_ORDER_INFO (state) {
    state.positionStatusLine = {}
    state.statuses = []
    state.positions = []
    state.consignments = []
    state.currentPosition = []
    state.comments = []
    state.activePositionIndex = 0
    state.currentPositionId = null
    state.currentPositionFormDataFilesDeleted = []
    state.materialForReserve = []
  },

  CLEAR_POSITION_DETAIL (state) {
    state.currentPosition = []
    state.currentPositionId = null
    state.positionStatusLine = {}
    state.statuses = []
    state.consignments = []
    state.comments = []
    state.materialForReserve = []
  },

  // comments
  SET_COMMENTS (state, data) {
    state.comments = Array.isArray(data) ? data : []
  },

  // gosts
  SET_GOSTS (state, data) {
    state.gosts = Array.isArray(data) ? data : []
  },

  // packing
  SET_PACKING (state, data) {
    const packing = Array.isArray(data) ? data : []
    state.packing = packing

    let sum = 0
    const positionsCount = {}
    packing.forEach(item => {
      let weightPositions = 0

      item.position_id_amount.forEach(positionAmount => {
        const position = state.positions?.find(position => position.id === positionAmount.position_id)

        sum += +positionAmount.amount
        weightPositions += position ? (+position?.weight * +positionAmount?.amount) : 0
        positionsCount[positionAmount.position_id] = (positionsCount[positionAmount.position_id] ?? 0) + +positionAmount.amount
        positionAmount.position_name = position ? `№${position.sort} ${position.type.name}` : 'Несуществующая позиция'
      })
      item.status_data = PACKING_STATUSES.find(status => status.value === item.status)

      const dimensions = (item?.package_type?.dimensions ? item?.package_type.dimensions + ', ' : '-, ')
      weightPositions += (item?.package_type?.weight ? +item.package_type.weight : 0)
      item.dimensions_weight = dimensions + fixedNumberCopy(weightPositions, 3) + ' кг'
    })

    state.packingPrepare = packing
    state.packingCount = sum
    state.packingPositionsCount = positionsCount
  },

  // После принятия партии в работу меняем in_operation и time_at_work_logs
  UPDATE_CONSIGNMENT_AFTER_ACCEPT (state, payload) {
    const index = state.consignments.findIndex((elem) => elem.id === payload.consignment_id)

    state.consignments[index].in_operation = true
    state.consignments[index].time_at_work_logs.unshift(payload)
  },

  // После добавления весов по резерву записываем их
  UPDATE_CONSIGNMENT_DIRTY_WEIGHTS (state, payload) {
    const index = state.consignments.findIndex((elem) => elem.id === payload.id)

    state.consignments[index].dirty_weights = payload.data
    const newWeightsDirty = payload?.data?.reduce((sum, item) => sum + (+item.weight_dirty || 0), 0)
    state.consignments[index].weight_dirty = newWeightsDirty ? fixedNumberCopy(newWeightsDirty, 3) : state.consignments[index].weight_dirty
  },

  SET_RESERVED_MATERIAL (state, data) {
    state.positions[state.activePositionIndex].reserved = Array.isArray(data) ? data : []
  },

  SET_MATERIAL_FOR_RESERVE (state, data) {
    state.materialForReserve = Array.isArray(data) ? data : []
  }
}

const actions = {
  async getOrderOne ({ state, commit, dispatch }, { id, positionIndex = 0 }) {
    commit('SET_ORDER_LOADING', true)
    try {
      const res = await OrderController.getById(id)
      if (res?.status !== 200) return goToPage('Order')
      const payload = getResponsePayload(res.data)
      commit('SET_ONE_ORDER', payload || {})

      if (payload?.waybills && payload.waybills[0]) {
        dispatch('setWaybill', payload.waybills)
      }

      // TODO вынести в отдельную функцию, т.к. это не используется в редактировании заказа
      if (payload?.positions) {
        commit('SET_POSITIONS', payload.positions)
        commit('SET_POSITION_ACTIVE_INDEX', positionIndex)
        commit('SET_CURRENT_POSITION_ID', positionIndex)
        commit('SET_CURRENT_POSITION_VALUE', { value: payload.positions[positionIndex] })
        commit('warehouse/SET_ARTICLE', state.currentPosition.article, { root: true })
        await dispatch('getGostsAll')
        await dispatch('getPackingAll')
      }
    } finally {
      commit('SET_ORDER_LOADING', false)
      // TODO вынести в отдельную функцию, т.к. это не используется в редактировании заказа
      commit('SET_CURRENT_POSITION_FILES_DELETE', null)
      dispatch('scroll/scrollToNode', {}, { root: true })
    }
  },

  setWaybill ({ commit }, waybills) {
    commit('waybill/SET_WAYBILL', waybills[0], { root: true })
  },

  async changeMassStatus ({ state, commit, dispatch, getters }, { id, direction }) {
    // TODO: запретить массовый переход если флаг delivery_permission: false, после интеграции с AmoCRM
    if (!getters.currentOrderDeliveryPermission) {
      await OrderController.updateDeliveryPermission(id)
    }

    const response = await OrderController.updateMassStatus(id, direction)
    if (response?.status !== 200) return
    await dispatch('getOrderOne', { id })
  },

  async restoreOrder ({ dispatch }, { id, externalId }) {
    const response = await OrderController.restoreOrder(id, externalId)
    if (response?.status !== 200) return
    await setAlertSuccess('Заказ восстановлен')
    await dispatch('getOrderOne', { id })
  },

  async changeOrderStatus ({ dispatch }, { id, status }) {
    const response = await OrderController.updateOrderStatus(id, status)
    if (response?.status !== 200) return
    await dispatch('getOrderOne', { id })
  },

  // eslint-disable-next-line camelcase
  async updateOrderMarkers ({ commit, dispatch }, { id, is_pickup, documents_are_ready, callback }) {
    const response = await OrderController.updateMarkers(id, { is_pickup, documents_are_ready })
    if (response?.status !== 200) return

    commit('SET_ORDER_READY_FOR_SHIPMENT_MARKERS', { is_pickup, documents_are_ready })
    callback()

    await setAlertSuccess('Заказ обновлен')
  },

  async addPacking ({ commit, dispatch }, data) {
    const response = await PackingController.create(data)
    if (response.status !== 201) return

    commit('SET_PACKING', getResponseList(response.data))
  },

  async updatePacking ({ commit, dispatch }, data) {
    const response = await PackingController.update(data.id, data)
    if (response.status !== 200) return

    commit('SET_PACKING', getResponseList(response.data))
  },

  async packingMassDelete ({ commit, dispatch }, data) {
    try {
      await instance.delete('/order/packages/mass', { params: data })
      dispatch('getPackingAll')
    } catch (e) {
      await setAlertError(e, 'Не удалось удалить упаковку')
    }
  },

  async packingMassChangeStatus ({ commit, dispatch }, data) {
    try {
      await instance.put('/order/packages', data)
      dispatch('getPackingAll')
    } catch (e) {
      await setAlertError(e, 'Не удалось обновить статусы упаковок')
    }
  },

  // position
  async loadCurrentPositionActive ({ commit, getters }) {
    const response = await PositionController.getById(getters.currentPositionId)
    const payload = getResponsePayload(response.data)
    commit('SET_CURRENT_POSITION_ACTIVE_VALUE', {
      data: payload,
      index: getters.activePositionIndex
    })
    // try {
    //   const response = await PositionController.getById(getters.currentPositionId)
    //   commit('SET_CURRENT_POSITION_ACTIVE_VALUE', {
    //     data: response.data,
    //     index: getters.activePositionIndex
    //   })
    // } catch (e) {
    //   await setAlertError(e, 'Не удалось загрузить позицию')
    // }
  },

  async createPosition ({ dispatch }, data) {
    const response = await PositionController.create(data)
    if (response.status !== 201) return

    await dispatch('responseProcessingCreate', getResponsePayload(response.data))
  },

  async createPositionByTemplate ({ dispatch }, data) {
    const response = await PositionApi.createByTemplate(data)
    await dispatch('responseProcessingCreate', getResponsePayload(response.data))
  },

  async responseProcessingCreate ({ commit, state, dispatch }, data) {
    data.reserved = []
    if (!data?.orders_in_work_by_article) data.orders_in_work_by_article = []
    commit('CLEAR_POSITION_DETAIL')
    commit('ADD_POSITION', data)
    commit('SET_CURRENT_POSITION_VALUE', { value: data })

    const index = state.positions.length ? state.positions.length - 1 : 0
    commit('SET_POSITION_ACTIVE_INDEX', index)
    commit('SET_CURRENT_POSITION_ID', index)
    commit('CHANGE_IS_EDIT_POSITION', true)
    commit('SET_POSITION_STATUS_LINE', index)
    commit('CHANGE_IS_CREATE_STAGE', true)
    await dispatch('getMaterialsForReserve')
  },

  async updatePositionCurrent ({ commit, state, getters, rootGetters, dispatch }, data) {
    const updateData = {
      ...data,
      changer_id: rootGetters.currentUserId
    }
    // Чтобы избежать случайного пересохранения workshop (он редактируется в отдельной форме)
    delete updateData.workshop_id
    delete updateData.workshop

    await PositionController.deleteFiles(state.currentPositionFormDataFilesDeleted)

    try {
      const response = await PositionController.update(data.id, updateData)
      if (response.status !== 200) return
      const payload = getResponsePayload(response.data)

      const isChangeWeight = +state.positions[getters.activePositionIndex].weight !== +data.weight

      if (isChangeWeight && state.consignments?.length) {
        data = {
          ...data,
          consignments: state.consignments.map((item, i) => ({
            ...item,
            weight_clear: +(payload?.consignments?.[i]?.weight_clear ?? item.weight_clear)
          }))
        }
      }
      const isChangeMaterialOrDWire =
        (state.positions[getters.activePositionIndex]?.material_id !== data?.material_id)
        || (state.positions[getters.activePositionIndex]?.d_wire !== data?.d_wire)

      if (payload?.order) commit('UPDATE_ORDER', payload.order)
      data = {
        ...data,
        waiting_start: payload?.waiting_start || 0
      }

      commit('SET_CURRENT_POSITION_VALUE', { value: data, id: data.id, isChangeWeight })
      commit('SET_CURRENT_POSITION_RESPONSIBLE', data.responsible)
      commit('SET_CURRENT_POSITION_NAME', data.name)
      commit('SET_CURRENT_POSITION_MATERIAL_CHILDREN', payload?.material)
      commit('CHANGE_IS_EDIT_POSITION', false)
      await dispatch('getPackingAll')

      if (isChangeMaterialOrDWire) {
        commit('SET_MATERIAL_FOR_RESERVE', [])
        await dispatch('getMaterialsForReserve')
      }
    } finally {
      const files = await PositionController.postFiles(data.id, data.files)
      if (files) {
        const filesPayload = getResponsePayload(files.data)
        state.positions[getters.activePositionIndex].files = filesPayload
        state.currentPosition.files = filesPayload
      }
      commit('SET_CURRENT_POSITION_FILES_DELETE', null)
    }
  },

  async removePosition ({ commit, dispatch, state }) {
    const response = await PositionController.destroy(state.currentPositionId)
    if (response?.status !== 204) return

    commit('REMOVE_POSITION', state.currentPositionId)
    await dispatch('getPackingAll')

    if (state.positions.length) {
      await dispatch('setActivePosition', 0)
    } else {
      commit('CLEAR_ORDER_INFO')
    }
  },

  async setActivePosition ({ commit, dispatch, getters }, index) {
    commit('SET_POSITION_ACTIVE_INDEX', index)
    commit('SET_CURRENT_POSITION_ID', index)
    commit('SET_CURRENT_POSITION', index)
    commit('SET_POSITION_STATUS_LINE', index)
    commit('SET_CONSIGNMENTS', index)
    commit('SET_MATERIAL_FOR_RESERVE', [])
    commit('CHANGE_IS_EDIT_POSITION', false)

    if (getters.currentPositionId) {
      await dispatch('getMaterialsForReserve')
      await dispatch('getCommentAll')
    }
  },

  async copyPosition ({ dispatch }, data) {
    const response = await PositionApi.copyPosition(data)
    if (response.status !== 201) return

    await dispatch('responseProcessingCreate', getResponsePayload(response.data))
  },

  async getMaterialsForReserve ({ commit, getters }) {
    if (!getters.currentPositionActive?.material?.article
      || !getters.currentPositionActive?.d_wire
    ) return

    const params = {
      article: getters.currentPositionActive?.material?.article,
      d_wire: getters.currentPositionActive?.d_wire
    }
    const response = await MaterialController.getMaterialsForReserve(params)
    if (response.status !== 200) return

    commit('SET_MATERIAL_FOR_RESERVE', getResponseList(response.data))
  },

  async setReserveMaterial ({ commit, dispatch }, { id, data }) {
    const response = await MaterialController.setReserve(id, data)
    if (response.status !== 200) return

    commit('SET_RESERVED_MATERIAL', getResponsePayload(response.data))
    await dispatch('getMaterialsForReserve')
  },

  // stage line
  async createStageLine ({ commit, state, dispatch }, statuses) {
    try {
      const postData = {
        position_id: state.currentPositionId,
        statuses
      }

      const response = await StageLineController.create(postData)
      dispatch('syncStageLine', getResponsePayload(response.data))
    } catch (error) {
      await setAlertError(error, 'Ошибка при создании линии этапов')
      commit('SET_STAGE_STORE', [])
      commit('SET_POSITION_STATUS_LINE_VALUE', [...defaultStageList])
    }
  },

  async updateStageLine ({ commit, state, dispatch }, statuses) {
    try {
      const stageLineId = state.positions[state.activePositionIndex].statuses_line.id
      const updateData = {
        position_id: state.currentPositionId,
        statuses
      }

      const response = await StageLineController.update(stageLineId, updateData)
      dispatch('syncStageLine', getResponsePayload(response.data))
    } catch (error) {
      await setAlertError(error, 'Ошибка при обновлении линии этапов')
    } finally {
      commit('CHANGE_IS_EDIT_STAGE', false)
    }
  },

  syncStageLine ({ commit }, data) {
    commit('SET_CURRENT_POSITION_STATUS_LINE', { id: data.id, statuses: data.statuses })
    commit('SET_POSITION_STATUS_LINE_VALUE', data.statuses)
    commit('SET_POSITION_STATUS_LINE_VALUE_TEMP', data.statuses)
    commit('CHANGE_IS_CREATE_STAGE', false)
  },

  // consignment
  async createConsignment ({ commit, getters, state, rootGetters, dispatch }, data) {
    const calcWeight = formulaTotalWeight(state.positions[state.activePositionIndex])
    const weightClear = fixedNumberCopy((calcWeight * data.value), 2)

    const postData = {
      changer_id: rootGetters.currentUserId,
      position_id: state.currentPositionId,
      status_id: data.stageId || state.statuses[0].status.id,
      weight_clear: weightClear,
      ...data,
      probes: data.probes || 0
    }

    try {
      const response = await instance.post('/consignment/consignments', postData)
      commit('ADD_CONSIGNMENT', getResponsePayload(response.data))
      commit('warehouse/COUNT_CALC_BY_ARTICLE', { article: data.article, value: -data.value }, { root: true })
    } catch (e) {
      await setAlertError(e, 'Не удалось создать партию')
    }
  },

  async updateConsignment ({ commit, rootGetters, dispatch }, consignment) {
    const postData = {
      changer_id: rootGetters.currentUserId,
      ...consignment
    }
    const articleData = {
      article: consignment.article,
      value: consignment.value_article
    }
    const params = {
      id: postData.id,
      updateData: postData
    }

    try {
      const res = await instance.put(`/consignment/consignments/${params.id}`, params.updateData)
      commit('UPDATE_CONSIGNMENT', getResponsePayload(res.data))
      commit('warehouse/COUNT_CALC_BY_ARTICLE', articleData, { root: true })
    } catch (e) {
      await setAlertError(e, 'Не удалось обновить партию')
    }
  },

  async getWeightDirtyWithLengthById ({ commit, getters }, id) {
    const response = await ConsignmentController.getWeightDirtyWithLength(id)
    commit('UPDATE_CONSIGNMENT_DIRTY_WEIGHTS', {
      data: getResponseList(response.data),
      id: id
    })
  },

  // comments
  async getCommentAll ({ commit, getters }, id) {
    try {
      const params = {
        position_id: id || getters.currentPositionId,
        sort: '-created_at'
      }

      const res = await getAll('consignment/comments', params)
      commit('SET_COMMENTS', getResponseList(res.data))
    } catch (e) {
      await setAlertError(e, 'Не удалось загрузить список комментариев')
    }
  },

  // gosts
  async getGostsAll ({ commit, getters }) {
    try {
      const res = await getAll(GOST_URL)
      commit('SET_GOSTS', getResponseList(res.data))
    } catch (e) {
      await setAlertError(e, 'Не удалось загрузить список Гостов')
    }
  },

  // packing
  async getPackingAll ({ commit, getters }) {
    const response = await PackingController.getById(getters.currentOrderId)
    commit('SET_PACKING', getResponseList(response.data))
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
